import React from "react";
import { 
  Shield, Lock, CreditCard, RotateCcw, AlertCircle, CheckCircle, 
  Truck, Info, Heart, UserX, Gavel, HelpCircle, RefreshCw 
} from "lucide-react";

const RefundPolicy = () => {
  const currentDate = new Date().toLocaleDateString("en-IN", {
    month: "long", day: "numeric", year: "numeric",
  });

  const highlights = [
    { icon: <RotateCcw size={20} />, text: "Damaged → Free Replacement" },
    { icon: <CreditCard size={20} />, text: "10% Bonus Store Credit" },
    { icon: <Shield size={20} />, text: "No Judgment – Zero Hassle" },
  ];

  return (
    <div className="min-h-screen bg-[#FFF0F3] text-gray-800 font-sans selection:bg-rose-200">
      <div className="h-2 bg-rose-500 w-full sticky top-0 z-50 shadow-md" />

      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Header */}
        <header className="text-center mb-16">
          <div className="inline-block px-5 py-2 rounded-full bg-rose-100 text-rose-700 text-base font-extrabold mb-6 tracking-wider uppercase shadow-sm">
            Fair & Transparent
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-5 tracking-tight leading-tight">
            Refund <span className="text-rose-500">Policy</span>
          </h1>
          <p className="text-xl text-gray-700 font-medium italic mb-6">
            Eveya – A brand of Farmfit Agrihealth LLP
          </p>
          <div className="flex justify-center items-center gap-6 text-base font-medium text-gray-600 flex-wrap">
            <span className="bg-white px-4 py-1.5 rounded-full border border-rose-200 shadow-sm">
              Last Updated: {currentDate}
            </span>
            <span className="text-rose-400 hidden sm:inline">•</span>
            <span className="bg-white px-4 py-1.5 rounded-full border border-rose-200 shadow-sm text-rose-600 font-bold">
              Customer First
            </span>
          </div>
        </header>

        {/* Highlights Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {highlights.map((item, index) => (
            <div key={index} className="bg-white p-7 rounded-2xl border border-rose-100 shadow-xl shadow-rose-200/50 flex items-center gap-5 transition-all hover:scale-105">
              <div className="p-4 bg-rose-500 rounded-xl text-white flex-shrink-0">{item.icon}</div>
              <span className="font-bold text-lg text-gray-800">{item.text}</span>
            </div>
          ))}
        </div>

        <div className="space-y-12">
          {/* 1. OVERVIEW */}
          <Section title="1. Refund Policy Overview" icon={<Info size={28} className="text-rose-500" />}>
            <p>Customer satisfaction is our priority. We handle all requests with transparency and zero judgment.</p>
            <div className="grid md:grid-cols-3 gap-4 mt-4">
              <div className="p-4 bg-white border border-rose-100 rounded-xl shadow-sm">
                <h4 className="font-bold text-rose-600 mb-1">Cash Refund</h4>
                <p className="text-xs text-gray-500">To original method • 5-7 business days</p>
              </div>
              <div className="p-4 bg-rose-500 text-white rounded-xl shadow-lg shadow-rose-200">
                <h4 className="font-bold mb-1">Store Credit</h4>
                <p className="text-xs opacity-90">Immediate • 10% Bonus Credit</p>
              </div>
              <div className="p-4 bg-white border border-rose-100 rounded-xl shadow-sm">
                <h4 className="font-bold text-rose-600 mb-1">Replacement</h4>
                <p className="text-xs text-gray-500">Free shipping • Defect-free product</p>
              </div>
            </div>
            
            <div className="mt-8 overflow-x-auto rounded-xl border border-rose-100">
                <table className="w-full text-sm text-left">
                    <thead className="bg-rose-50 text-rose-700 font-bold uppercase text-[10px] tracking-wider">
                        <tr><th className="p-4">Scenario</th><th className="p-4">Window</th><th className="p-4">Applicability</th></tr>
                    </thead>
                    <tbody className="divide-y divide-rose-50">
                        <tr><td className="p-4">Damaged/Defective</td><td className="p-4">7 Days</td><td className="p-4">Everyone</td></tr>
                        <tr><td className="p-4">Unopened Boxes</td><td className="p-4">30 Days</td><td className="p-4 text-rose-500 font-bold">Subscribers Only</td></tr>
                        <tr><td className="p-4">Pregnancy/Nursing</td><td className="p-4">60 Days</td><td className="p-4 italic">Health Exception</td></tr>
                    </tbody>
                </table>
            </div>
          </Section>

          {/* 2. NON-SUBSCRIBERS */}
          <Section title="2. Non-Subscriber Policy" icon={<UserX size={28} className="text-rose-500" />}>
            <p>Non-subscribers may request refunds <strong>ONLY</strong> for damaged, defective, or incorrect items. "Change of mind" is not applicable.</p>
            <div className="bg-rose-50 p-6 rounded-2xl border-l-4 border-rose-500">
                <p className="font-bold text-rose-700 mb-2">No Return Required:</p>
                <p className="text-sm">For damaged goods, non-subscribers do not need to ship the item back. Just provide documentation (Photo/Video) and we will process your refund/replacement.</p>
            </div>
          </Section>

          {/* 3. SUBSCRIPTIONS */}
          <Section title="3. Subscriptions & Cancellations" icon={<RefreshCw size={28} className="text-rose-500" />}>
            <p>Subscribers enjoy extended 30-day windows for unopened returns. <strong>Subscription Cancellation:</strong></p>
            <ul className="list-disc pl-5 space-y-2 text-sm">
                <li>Cancellation takes effect on the <strong>next renewal date</strong>. No mid-cycle refunds.</li>
                <li><strong>No Penalties:</strong> Zero cancellation fees or early termination charges.</li>
                <li><strong>No Pausing:</strong> Currently, subscriptions cannot be paused or skipped mid-cycle.</li>
            </ul>
          </Section>

          {/* 4. SAKHI SPONSORSHIP */}
          <Section title="4. Sakhi Sponsorship Program" icon={<Heart size={28} className="text-rose-500" />}>
            <div className="bg-rose-500 text-white p-6 rounded-2xl shadow-xl">
                <h4 className="font-bold flex items-center gap-2 mb-2"><AlertCircle size={18}/> Non-Refundable Nature</h4>
                <p className="text-sm opacity-90">Sakhi sponsorships are final. Once payment is processed, funds are immediately allocated to beneficiaries and cannot be refunded or exchanged.</p>
                <p className="text-[10px] mt-4 uppercase tracking-widest font-bold">Only damaged products reported within 7 days are eligible for replacement.</p>
            </div>
          </Section>

          {/* 5. DEFECTIVE CLAIMS */}
          <Section title="5. Defective Product Claims" icon={<AlertCircle size={28} className="text-rose-500" />}>
            <p><strong>What qualifies:</strong> Leaking applicators, mold/contamination, expired products, or damaged packaging seals.</p>
            <div className="bg-amber-50 p-5 rounded-xl border border-amber-200 flex gap-4">
                <Info className="text-amber-500 shrink-0" />
                <p className="text-sm text-amber-900 font-medium"><strong>Allergies:</strong> Skin sensitivities are personal and not manufacturing defects. If irritation occurs, discontinue use and contact us for hypoallergenic alternatives.</p>
            </div>
          </Section>

          {/* 6. PROCESSING STEPS */}
          <Section title="6. Refund Timeline (The Phase-Way)" icon={<Truck size={28} className="text-rose-500" />}>
            <div className="space-y-4">
                {[
                    { phase: "Approval", time: "Day 1", desc: "Request verified" },
                    { phase: "Transit/Inspection", time: "Days 2-9", desc: "Item received at warehouse & inspected" },
                    { phase: "Initiation", time: "Days 10-12", desc: "Refund sent to payment gateway" },
                    { phase: "Bank Processing", time: "Days 13-20", desc: "Funds appear in your account" }
                ].map((step, i) => (
                    <div key={i} className="flex items-center gap-4 bg-white p-3 border border-rose-50 rounded-lg">
                        <span className="font-bold text-rose-500 w-16">{step.time}</span>
                        <div className="h-2 w-2 rounded-full bg-rose-200" />
                        <div>
                            <p className="font-bold text-sm">{step.phase}</p>
                            <p className="text-xs text-gray-500">{step.desc}</p>
                        </div>
                    </div>
                ))}
            </div>
          </Section>

          {/* 9. CHARGEBACKS */}
          <Section title="9. Chargebacks & Disputes" icon={<Gavel size={28} className="text-rose-500" />}>
            <p className="text-sm">Contacting your bank before us (Chargeback) takes <strong>60+ days</strong> and flags your account as high-risk. We resolve refunds in <strong>7 days</strong>. Unjustified chargebacks result in account suspension.</p>
          </Section>

          {/* 10. ESCALATIONS */}
          <Section title="10. Contact & Escalations" icon={<HelpCircle size={28} className="text-rose-500" />}>
            <div className="grid md:grid-cols-2 gap-6">
                <div className="p-6 bg-white border-2 border-dashed border-rose-200 rounded-2xl text-center">
                    <p className="text-xs text-gray-500 font-bold uppercase mb-2">Primary Support</p>
                    <a href="mailto:support@eveya.in" className="text-lg font-bold text-rose-600 block">support@eveya.in</a>
                    <p className="text-xs text-gray-400">Response within 48h</p>
                </div>
                <div className="p-6 bg-white border-2 border-dashed border-rose-200 rounded-2xl text-center">
                    <p className="text-xs text-gray-500 font-bold uppercase mb-2">WhatsApp Help</p>
                    <a href="tel:+918121514499" className="text-lg font-bold text-rose-600 block">+91 8121514499</a>
                    <p className="text-xs text-gray-400">Response within 24h</p>
                </div>
            </div>
          </Section>

          {/* Footer */}
          {/* <footer className="mt-24 text-center text-gray-600 text-sm pb-12 border-t border-rose-200 pt-12">
            <p className="font-bold text-gray-900">Farmfit Agrihealth LLP</p>
            <p className="text-xs mt-1 text-gray-400">Compliant with Consumer Protection Act (2019) & DPDP Act (2023)</p>
            <p className="mt-6 uppercase tracking-[0.2em] font-black text-xs text-rose-500">
              Wellness • Fairness • Trust
            </p>
          </footer> */}
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, icon, children }) => (
  <section className="bg-white rounded-3xl p-9 border border-rose-100 shadow-xl shadow-rose-200/40 hover:shadow-rose-300/50 transition-all duration-300">
    <div className="flex items-center gap-5 mb-7">
      {icon && (
        <div className="p-3.5 bg-gradient-to-br from-rose-50 to-rose-100 rounded-2xl shadow-sm">
          {icon}
        </div>
      )}
      <h2 className="text-3xl font-bold text-gray-900 tracking-tight">{title}</h2>
    </div>
    <div className="space-y-5 text-gray-700 leading-relaxed text-[15.5px]">
      {children}
    </div>
  </section>
);

export default RefundPolicy;